import {
  Component,
  OnDestroy,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  takeUntil,
  Subject,
  debounceTime,
  distinctUntilChanged,
  combineLatestWith,
} from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { Order, OrderDynamicField } from 'src/app/models/order.model';
import { OrderStage } from 'src/app/enums/order-stage';
import { OrderStatus, orderStatusLabel } from 'src/app/enums/order-status';
import { OrdersService } from 'src/app/services/orders.service';
import { FilterOptions } from 'src/app/models/filter.model';
import {
  OrderTableColumn,
  PaginationData,
  ResponseData,
} from 'src/app/models/helpers.model';
import { LoadingService } from 'src/app/services/loading.service';
import { WorkSheet, WorkBook, utils, writeFile } from 'xlsx';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { Router } from '@angular/router';
import { OrderFieldService } from 'src/app/services/order-field.service';
import { NullTransformPipe } from 'src/app/dashboard/pipes/null-transform.pipe';
import { FieldType } from 'src/app/enums/field-type';
import { FilterOperator } from 'src/app/enums/filter-operator';
import { OrderField } from 'src/app/models/field-list.model';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CountriesService } from 'src/app/services/countries.service';
import { Country } from 'src/app/models/country.model';
import { ToastrService } from 'ngx-toastr';
import { FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import * as moment from 'moment';
import { HeaderStateService } from 'src/app/services/header-state.service';
import { CompanyScopeService } from 'src/app/services/company-scope.service';

// Stamped into the saved column layout ('table' / 'reports_table'). The default
// order is the order the API returns its fields in, which is the order the
// redesign specifies — but a layout saved before a column existed keeps that
// column wherever it was appended when it first showed up (HC# landed after
// Modified instead of ahead of Dest. Country). Bumping this discards such a
// layout once, so the order is re-seeded from the API; a user's own reordering
// after that is kept.
const COLUMN_LAYOUT_VERSION = 2;

@Component({
  selector: 'app-orders-table',
  templateUrl: './orders-table.component.html',
  styleUrls: ['./orders-table.component.scss'],
  providers: [NullTransformPipe, DatePipe],
})
export class OrdersTableComponent implements OnInit, OnDestroy {
  @Input() pageName!: string;
  @ViewChild('exportModal') exportModalEl!: ElementRef;
  countries: Country[] = [];
  countriesForSearch: Country[] = [];
  searchCountries: FormControl = new FormControl('');
  customFieldType = FieldType;
  orderStatus = OrderStatus;
  statusLabel = orderStatusLabel;
  orderStage = OrderStage;
  paginationData!: PaginationData;
  showFilterModal = false;
  isOrdersLoaded = false;
  orders!: Order[];
  o!: Order[];
  dynamicFields!: OrderField[];
  ordersForXslx: Order[] = [];
  ordersStats = { open: 0, onHold: 0, closed: 0, canceled: 0 };
  selectedCol = 'poNumber';
  // Which status tab is lit. Bound in the template, so it is also what marks the
  // tab the page opens on (see ngOnInit).
  activeStatusTab: 'all' | 'open' | 'onHold' | 'canceled' | 'closed' = 'all';
  filteredOrders!: any[];
  filterValue = '';
  tableColumns: string[] = [];
  reorderedColumns: string[] = [];
  reorderedColumnsB: string[] = [];
  columnsStates: boolean[] = [];
  columnsStatesBackup: boolean[] = [];
  columnsStatesBackupB: boolean[] = [];
  selectedFilter!: string;
  fieldType = FieldType;
  filteredFieldType = FieldType.Number;
  dataSource: any;
  isApplyFilters = true;
  summaryOptions: FilterOptions = {
    advancedSearch: {
      fields: [],
      keyword: '',
    },
    keyword: '',
    advancedFilter: null,
  };
  searchOptions: FilterOptions = {
    advancedSearch: {
      fields: [
        'poNumber',
        'orderNumber',
        'consignee.name',
        'customer.name',
        'coldStorage.name',
        // HC# search; the backend strips this field for roles without permission to view it.
        'healthCertificateNumber',
      ],
      keyword: '',
    },
    keyword: '',
    advancedFilter: null,
    pageNumber: 1,
    pageSize: 15,
    orderBy: [],
  };
  stageEnumValues = [
    { name: 'Stage 1', value: OrderStage.Stage1 },
    { name: 'Stage 2A', value: OrderStage.Stage2A },
    { name: 'Stage 2B', value: OrderStage.Stage2B },
    { name: 'Stage 3', value: OrderStage.Stage3 },
    { name: 'Stage 4', value: OrderStage.Stage4 },
  ];
  statusEnumValues = [
    { name: 'Open', value: OrderStatus.Open },
    { name: 'On Hold', value: OrderStatus.OnHold },
    { name: 'Canceled', value: OrderStatus.Canceled },
    { name: 'Closed', value: OrderStatus.Closed },
  ];
  searchOptionsAlt: FilterOptions = {
    advancedSearch: {
      fields: [],
      keyword: '',
    },
    keyword: '',
    advancedFilter: null,
    pageNumber: 1,
    pageSize: 1,
    orderBy: [],
  };
  searchString = '';
  selectedSort = { field: '', mode: '' };
  rangeFilter!: Array<{
    field: string;
    operator: string;
    value: string;
  }> | null;
  statusFilter!: {
    field: string;
    operator: string;
    value: string;
  } | null;
  customFilter!: any;
  allFieldFilters: Array<{
    name: string;
    type: 'text' | 'number' | 'bool' | 'enum' | 'date' | 'list';
    field: string;
    operator: string;
    value: string;
    isShow: boolean;
  }> = [];
  multiFilters: any;
  private destroy = new Subject<boolean>();
  private search = new Subject<string>();
  displayedColumns: string[] = [];
  tableColumnsObj: OrderTableColumn[] = [];
  reorderedColumnsObj: OrderTableColumn[] = [];

  constructor(
    private ordersService: OrdersService,
    private loadingService: LoadingService,
    private router: Router,
    private orderFieldService: OrderFieldService,
    private countriesService: CountriesService,
    private nullTransform: NullTransformPipe,
    private headerState: HeaderStateService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private companyScopeService: CompanyScopeService
  ) {}

  // ECDEV-256: the saved column layout and filters are shaped by the active company — the column
  // set comes from that company's dynamic fields — so they are stored per scope. Without this,
  // switching applies one company's columns and filter values to another's data. "All companies"
  // gets its own bucket too, since it shows an extra company column.
  private scopedKey(key: string): string {
    return `${key}:${this.companyScopeService.currentScopeKey()}`;
  }

  private scopedGet(key: string): string | null {
    return localStorage.getItem(this.scopedKey(key));
  }

  private scopedSet(key: string, value: string): void {
    localStorage.setItem(this.scopedKey(key), value);
  }

  private scopedRemove(key: string): void {
    localStorage.removeItem(this.scopedKey(key));
  }

  ngOnInit(): void {
    // Orders opens on Open — that is the working set; All/Canceled/Closed are a
    // click away. Reports has no status tabs, so it stays unfiltered.
    // The filter has to be in place before the first load: the date-range
    // subject emits on subscribe below and that emission is what fetches the
    // first page.
    if (this.pageName === 'orders') {
      this.activeStatusTab = 'open';
      this.statusFilter = {
        field: 'status',
        operator: FilterOperator.Equals,
        value: OrderStatus.Open.toString(),
      };
    }
    this.getFilterFields();
    this.filteredOrders = this.orders;
    this.search.pipe(debounceTime(2000), distinctUntilChanged()).subscribe({
      next: () => {
        this.searchOptions.advancedSearch.keyword = this.searchString;
        this.searchOptions.pageNumber = 1;
        this.getOrders(this.searchOptions);
      },
    });
    // date-range control lives in the dashboard header
    this.headerState.dateRange
      .pipe(
        distinctUntilChanged((a, b) => a.gte === b.gte && a.lte === b.lte),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: (range) => this.getRange(range),
      });
    this.getCountries();
    this.searchCountries.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: (value: string) => {
        this.countries = this.countriesForSearch.filter((c: Country) =>
          c.name.toLowerCase().includes(value.toLowerCase())
        );
      },
    });
  }

  isSettedFilter(col: string) {
    return (
      this.allFieldFilters.find((f) => f.name === col)?.value !== '' &&
      this.allFieldFilters.find((f) => f.name === col)?.operator !== ''
    );
  }

  getRange(range: { gte: string; lte: string }) {
    if (range.gte) {
      this.rangeFilter = [
        {
          field: 'createdOn',
          operator: FilterOperator.GreaterThanOrEqual,
          value: range.gte,
        },
        {
          field: 'createdOn',
          operator: FilterOperator.LessThanOrEqual,
          value: range.lte,
        },
      ];
      this.searchOptions.advancedFilter = {
        logic: 'and',
        filters: this.statusFilter
          ? [this.statusFilter, ...this.rangeFilter]
          : [...this.rangeFilter],
      };
    } else {
      this.rangeFilter = null;
      if (this.statusFilter) {
        this.searchOptions.advancedFilter = {
          logic: 'and',
          filters: [this.statusFilter],
        };
      } else {
        this.searchOptions.advancedFilter = null;
      }
    }
    this.applyMultiFilters();
  }

  getSummary(status: OrderStatus, searchOptions?: any[]) {
    const statusFilter = {
      field: 'status',
      operator: FilterOperator.Equals,
      value: status.toString(),
    };
    this.summaryOptions.advancedFilter = {
      logic: 'and',
      filters:
        searchOptions && this.rangeFilter?.length
          ? [statusFilter, ...searchOptions, ...this.rangeFilter]
          : !searchOptions && this.rangeFilter?.length
          ? [statusFilter, ...this.rangeFilter]
          : searchOptions && !this.rangeFilter?.length
          ? [statusFilter, ...searchOptions]
          : [statusFilter],
    };
    this.loadingService.changeLoadingStatus(true);
    this.ordersService
      .getOrdersSummary(this.summaryOptions)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp: number) => {
          if (status === this.orderStatus.Open) {
            this.ordersStats.open = resp;
          }
          if (status === this.orderStatus.OnHold) {
            this.ordersStats.onHold = resp;
          }
          if (status === this.orderStatus.Closed) {
            this.ordersStats.closed = resp;
          }
          if (status === this.orderStatus.Canceled) {
            this.ordersStats.canceled = resp;
          }

          this.loadingService.changeLoadingStatus(false);
        },
        error: () => {
          this.loadingService.changeLoadingStatus(false);
        },
      });
  }

  getCountries() {
    this.countriesService.contriesList.pipe(takeUntil(this.destroy)).subscribe({
      next: (resp: Country[] | null) => {
        if (resp) {
          this.countries = resp.sort((a: Country, b: Country) => {
            return a.name.toLowerCase() < b.name.toLowerCase()
              ? -1
              : a.name.toLowerCase() > b.name.toLowerCase()
              ? 1
              : 0;
          });
          this.countriesForSearch = resp;
        }
      },
    });
  }

  checkIsEqual(a: Array<any>, b: Array<any>): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      a = a.sort();
      b = b.sort();
      return a.length === b.length && a.every((el, i) => el === b[i]);
    }
    return false;
  }

  // Reads the saved column layout, or null when there is none / it predates
  // COLUMN_LAYOUT_VERSION. A stale one is dropped along with the column
  // fingerprint it was reconciled against, so the layout is re-seeded from the
  // order the API returns its fields in.
  private readSavedColumnLayout(layoutKey: string, fingerprintKey: string) {
    const raw = this.scopedGet(layoutKey);
    if (!raw) {
      return null;
    }

    let saved: any = null;
    try {
      saved = JSON.parse(raw);
    } catch {
      saved = null;
    }

    if (!saved || saved.layoutVersion !== COLUMN_LAYOUT_VERSION) {
      this.scopedRemove(layoutKey);
      this.scopedRemove(fingerprintKey);
      return null;
    }

    return saved;
  }

  getFilterFields() {
    const savedLayout =
      this.pageName === 'orders'
        ? this.readSavedColumnLayout('table', 'tc_saved_orders')
        : this.readSavedColumnLayout('reports_table', 'tc_saved_reports');

    if (savedLayout) {
      this.tableColumns = [...savedLayout.tableColumns];
      this.reorderedColumns = [...savedLayout.reorderedColumns];
      this.columnsStates = [...savedLayout.columnsStates];
      this.columnsStatesBackup = [...savedLayout.columnsStates];
    } else {
      this.tableColumns = [];
      this.reorderedColumns = [];
      this.columnsStates = [];
      this.columnsStatesBackup = [];
    }

    this.reorderedColumnsB = [];
    this.columnsStatesBackupB = [];
    this.loadingService.changeLoadingStatus(true);
    this.ordersService
      .getOrders(this.searchOptionsAlt)
      .pipe(
        combineLatestWith(this.orderFieldService.getFields()),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([resp, dynamicFields]) => {
          this.o = resp.data.map((order: any) => {
            const fields = [];
            for (let field of dynamicFields) {
              fields.push({
                name: field.name,
                type: field.type,
                isCustom: true,
                value: order.dynamicFields.find(
                  (df: OrderDynamicField) => df.dynamicFieldId === field.id
                )?.fieldValueDate
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueDate
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueStr
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueStr
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueNum
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueNum
                  : '',
              });
            }
            for (let f of fields) {
              order = {
                ...order,
                [f.name]: f,
              };
            }
            return order;
          });

          const tc: any[] = [];
          const cs: any[] = [];
          const rc: any[] = [];
          const csb: any[] = [];

          for (let key in this.o[0]) {
            if (
              key !== 'coldStorageId' &&
              key !== 'consigneeId' &&
              key !== 'customerId' &&
              key !== 'id' &&
              key !== 'isNew' &&
              key !== 'dynamicFields' &&
              key !== 'stage1ApprovedOn' &&
              key !== 'stage2AApprovedOn' &&
              key !== 'stage2BApprovedOn' &&
              key !== 'stage1NotifiedOn' &&
              key !== 'stage2ANotifiedOn' &&
              key !== 'stage2BNotifiedOn' &&
              key !== 'stage3ApprovedOn' &&
              key !== 'stage4ApprovedOn' &&
              key !== 'isCompleted' &&
              key !== 'stage3CmpInventoryApprovedOn' &&
              key !== 'stage3CustomerApprovedOn'
            ) {
              tc.push(key);
              cs.push(true);
              rc.push(key);
              csb.push(true);
              this.reorderedColumnsB.push(key);
              this.columnsStatesBackupB.push(true);
              this.allFieldFilters.push({
                name: key,
                type:
                  key === 'orderNumber' ||
                  key === 'poNumber' ||
                  key === 'customerName' ||
                  key === 'consigneeName' ||
                  key === 'coldStorageName' ||
                  key === 'countryId' ||
                  key === 'healthCertificateNumber' ||
                  this.o[0][key]?.type === FieldType.Text ||
                  this.o[0][key]?.type === FieldType.List
                    ? 'text'
                    : this.o[0][key]?.type === FieldType.Number
                    ? 'number'
                    : key === 'createdOn' ||
                      key === 'lastModifiedOn' ||
                      this.o[0][key]?.type === FieldType.DateTime
                    ? 'date'
                    : key === 'stage' || key === 'status'
                    ? 'enum'
                    : 'bool',
                field:
                  key === 'customerName'
                    ? 'customer.name'
                    : key === 'consigneeName'
                    ? 'consignee.name'
                    : key === 'coldStorageName'
                    ? 'coldStorage.name'
                    : (this.o[0][key] &&
                        this.o[0][key].type === FieldType.Text) ||
                      (this.o[0][key] && this.o[0][key].type === FieldType.List)
                    ? 'dynamicFields.fieldValueStr'
                    : this.o[0][key] && this.o[0][key].type === FieldType.Number
                    ? 'dynamicFields.fieldValueNum'
                    : this.o[0][key] &&
                      this.o[0][key].type === FieldType.DateTime
                    ? 'dynamicFields.fieldValueDate'
                    : key,
                operator: 'Equals',
                value: '',
                isShow: true,
              });
            }
          }

          if (
            this.pageName === 'orders' &&
            !this.scopedGet('tc_saved_orders')
          ) {
            this.tableColumns = [...tc];
            this.reorderedColumns = [...rc];
            this.columnsStates = [...cs];
            this.columnsStatesBackup = [...csb];
          }

          if (
            this.pageName === 'orders' &&
            this.scopedGet('tc_saved_orders')
          ) {
            if (
              !this.checkIsEqual(
                [...tc],
                [...JSON.parse(<string>this.scopedGet('tc_saved_orders'))]
              ) ||
              !this.tableColumns.length
            ) {
              rc.forEach((el, apiIndex) => {
                if (!this.reorderedColumns.includes(el)) {
                  // Slot a newly appeared column where the API puts it rather
                  // than appending it: that is how HC# once ended up to the
                  // right of Modified instead of ahead of Dest. Country.
                  const at = Math.min(apiIndex, this.reorderedColumns.length);
                  this.reorderedColumns.splice(at, 0, el);
                  this.columnsStates.splice(at, 0, true);
                }
              });
              const tempReorderedC = [...this.reorderedColumns];
              tempReorderedC.forEach((el) => {
                if (!rc.includes(el)) {
                  let index = this.reorderedColumns.indexOf(el);
                  this.reorderedColumns.splice(index, 1);
                  this.columnsStates.splice(index, 1);
                }
              });
              this.columnsStatesBackup = [...this.columnsStates];
            }
          }

          if (
            this.pageName !== 'orders' &&
            !this.scopedGet('tc_saved_reports')
          ) {
            this.tableColumns = [...tc];
            this.reorderedColumns = [...rc];
            this.columnsStates = [...cs];
            this.columnsStatesBackup = [...csb];
          }

          if (
            this.pageName !== 'orders' &&
            this.scopedGet('tc_saved_reports')
          ) {
            if (
              !this.checkIsEqual(
                [...tc],
                [
                  ...JSON.parse(
                    <string>this.scopedGet('tc_saved_reports')
                  ),
                ]
              ) ||
              !this.tableColumns.length
            ) {
              rc.forEach((el, apiIndex) => {
                if (!this.reorderedColumns.includes(el)) {
                  // Slot a newly appeared column where the API puts it rather
                  // than appending it: that is how HC# once ended up to the
                  // right of Modified instead of ahead of Dest. Country.
                  const at = Math.min(apiIndex, this.reorderedColumns.length);
                  this.reorderedColumns.splice(at, 0, el);
                  this.columnsStates.splice(at, 0, true);
                }
              });
              const tempReorderedC = [...this.reorderedColumns];
              tempReorderedC.forEach((el) => {
                if (!rc.includes(el)) {
                  let index = this.reorderedColumns.indexOf(el);
                  this.reorderedColumns.splice(index, 1);
                  this.columnsStates.splice(index, 1);
                }
              });
              this.columnsStatesBackup = [...this.columnsStates];
            }
          }

          if (this.pageName === 'orders') {
            if (
              !this.scopedGet('tc_saved_orders') ||
              !this.checkIsEqual(
                [...tc],
                [...JSON.parse(<string>this.scopedGet('tc_saved_orders'))]
              )
            ) {
              this.scopedRemove('tc_saved_orders');
              this.scopedSet('tc_saved_orders', JSON.stringify(tc));
            }
          } else {
            if (
              !this.scopedGet('tc_saved_reports') ||
              !this.checkIsEqual(
                [...tc],
                [
                  ...JSON.parse(
                    <string>this.scopedGet('tc_saved_reports')
                  ),
                ]
              )
            ) {
              this.scopedRemove('tc_saved_reports');
              this.scopedSet('tc_saved_reports', JSON.stringify(tc));
            }
          }

          if (this.pageName === 'orders' && this.scopedGet('multiFO')) {
            const savedFO = [
              ...JSON.parse(<string>this.scopedGet('multiFO')),
            ];
            this.allFieldFilters.forEach((f) => {
              savedFO.forEach((fo) => {
                if (f.name === fo.name) {
                  f.value = fo.value;
                  f.operator = fo.operator;
                }
              });
            });
          }

          if (this.pageName !== 'orders' && this.scopedGet('multiFR')) {
            const savedFR = [
              ...JSON.parse(<string>this.scopedGet('multiFR')),
            ];
            this.allFieldFilters.forEach((f) => {
              savedFR.forEach((fr) => {
                if (f.name === fr.name) {
                  f.value = fr.value;
                  f.operator = fr.operator;
                }
              });
            });
          }
        },
      });
  }

  getOrders(searchOptions: FilterOptions, isInitial = true) {
    this.resetLoadedStatus();
    this.tableColumnsObj = [];
    this.reorderedColumnsObj = [];
    this.loadingService.changeLoadingStatus(true);
    this.ordersService
      .getOrders(searchOptions)
      .pipe(
        combineLatestWith(this.orderFieldService.getFields()),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([resp, dynamicFields]) => {
          this.dynamicFields = dynamicFields;
          this.orders = resp.data.map((order: any) => {
            const fields = [];
            for (let field of dynamicFields) {
              fields.push({
                id: field.id,
                name: field.name,
                type: field.type,
                isCustom: true,
                value: order.dynamicFields.find(
                  (df: OrderDynamicField) => df.dynamicFieldId === field.id
                )?.fieldValueDate
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueDate
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueStr
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueStr
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueNum
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueNum
                  : '',
              });
            }
            for (let f of fields) {
              order = {
                ...order,
                [f.name]: f,
              };
            }

            return order;
          });

          for (let key in this.orders[0]) {
            if (
              key !== 'coldStorageId' &&
              key !== 'consigneeId' &&
              key !== 'customerId' &&
              key !== 'id' &&
              key !== 'isNew' &&
              key !== 'dynamicFields' &&
              key !== 'stage1ApprovedOn' &&
              key !== 'stage2AApprovedOn' &&
              key !== 'stage2BApprovedOn' &&
              key !== 'stage1NotifiedOn' &&
              key !== 'stage2ANotifiedOn' &&
              key !== 'stage2BNotifiedOn' &&
              key !== 'stage3ApprovedOn' &&
              key !== 'stage4ApprovedOn' &&
              key !== 'isCompleted' &&
              key !== 'stage3CmpInventoryApprovedOn' &&
              key !== 'stage3CustomerApprovedOn'
            ) {
              this.tableColumnsObj.push({
                columnDef: key,
                header:
                  key === 'poNumber'
                    ? 'p.o. no.'
                    : key === 'orderNumber'
                    ? 'order no.'
                    : key === 'customerName'
                    ? 'customer'
                    : key === 'consigneeName'
                    ? 'consignee'
                    : key === 'coldStorageName'
                    ? 'cold storage'
                    : key === 'healthCertificateNumber'
                    ? 'hc #'
                    : key === 'countryId'
                    ? 'dest. country'
                    : key === 'createdOn'
                    ? 'created'
                    : key === 'lastModifiedOn'
                    ? 'modified'
                    : key,
                isShow: true,
                isShowBackup: true,
                isCustom: this.orders[0][key]?.name ? true : false,
                fieldId: this.orders[0][key]?.id
                  ? this.orders[0][key]?.id
                  : null,
                type:
                  this.orders[0][key]?.type !== undefined
                    ? this.orders[0][key]?.type
                    : null,
                filterKey:
                  key === 'customerName'
                    ? 'customer.name'
                    : key === 'consigneeName'
                    ? 'consignee.name'
                    : key === 'coldStorageName'
                    ? 'coldStorage.name'
                    : key,
                cell: (order: Order) =>
                  `${this.nullTransform.transform(order[key as keyof Order])}`,
                cell_alt: (order: Order) =>
                  `${order[key as keyof Order].value}`,
                cell_id: (order: Order) =>
                  `${
                    this.countriesService.countriesArray.find(
                      (c) => c.id === order[key as keyof Order]
                    )?.name
                  }`,
              });

              this.reorderedColumnsObj.push({
                columnDef: key,
                header:
                  key === 'poNumber'
                    ? 'p.o. no.'
                    : key === 'orderNumber'
                    ? 'order no.'
                    : key === 'customerName'
                    ? 'customer'
                    : key === 'consigneeName'
                    ? 'consignee'
                    : key === 'coldStorageName'
                    ? 'cold storage'
                    : key === 'healthCertificateNumber'
                    ? 'hc #'
                    : key === 'countryId'
                    ? 'dest. country'
                    : key === 'createdOn'
                    ? 'created'
                    : key === 'lastModifiedOn'
                    ? 'modified'
                    : key,
                isShow: true,
                isShowBackup: true,
                isCustom: this.orders[0][key]?.name ? true : false,
                fieldId: this.orders[0][key]?.id
                  ? this.orders[0][key]?.id
                  : null,
                type:
                  this.orders[0][key]?.type !== undefined
                    ? this.orders[0][key]?.type
                    : null,
                filterKey:
                  key === 'customerName'
                    ? 'customer.name'
                    : key === 'consigneeName'
                    ? 'consignee.name'
                    : key === 'coldStorageName'
                    ? 'coldStorage.name'
                    : key,
                cell: (order: Order) =>
                  `${this.nullTransform.transform(order[key as keyof Order])}`,
                cell_alt: (order: Order) =>
                  `${order[key as keyof Order].value}`,
                cell_id: (order: Order) =>
                  `${
                    this.countriesService.countriesArray.find(
                      (c) => c.id === order[key as keyof Order]
                    )?.name
                  }`,
              });
            }
          }

          if (isInitial) {
            this.applyFilters();
          }

          this.displayedColumns = this.tableColumnsObj.map((c) => c.columnDef);
          const { data, ...clone } = resp;
          this.paginationData = clone;
          this.updateHeaderBadge();
          this.dataSource = new MatTableDataSource(this.orders);
          this.selectedFilter = '';
          this.isOrdersLoaded = true;
          this.loadingService.changeLoadingStatus(false);
        },
        error: () => {
          this.selectedFilter = '';
          this.isOrdersLoaded = true;
          this.loadingService.changeLoadingStatus(false);
        },
      });
  }

  resetLoadedStatus() {
    this.isOrdersLoaded = false;
  }

  filterByStatus(
    selector: 'all' | 'open' | 'onHold' | 'canceled' | 'closed',
    status?: OrderStatus
  ) {
    this.activeStatusTab = selector;

    this.multiFilters = this.allFieldFilters
      .map((f) => {
        if (f.operator === 'Any') {
          return {
            logic: 'and',
            filters: [
              {
                field: f.field,
                operator: FilterOperator.NotEqual,
                value: null,
              },
              {
                field: f.field,
                operator: FilterOperator.NotEqual,
                value: 0,
              },
            ],
          };
        }

        if (
          (f.name === 'createdOn' || f.name === 'lastModifiedOn') &&
          f.operator === 'Equals' &&
          f.value
        ) {
          return {
            logic: 'and',
            filters: [
              {
                field: f.field,
                operator: FilterOperator.GreaterThanOrEqual,
                value:
                  moment(f.value).startOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                  '.001Z',
              },
              {
                field: f.field,
                operator: FilterOperator.LessThanOrEqual,
                value:
                  moment(f.value).endOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                  '.999Z',
              },
            ],
          };
        }

        if (
          (f.name === 'createdOn' || f.name === 'lastModifiedOn') &&
          f.operator === '!Equals' &&
          f.value
        ) {
          return {
            logic: 'or',
            filters: [
              {
                field: f.field,
                operator: FilterOperator.LessThan,
                value:
                  moment(f.value).startOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                  '.001Z',
              },
              {
                field: f.field,
                operator: FilterOperator.GreaterThan,
                value:
                  moment(f.value).endOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                  '.999Z',
              },
            ],
          };
        }

        return {
          field: f.field,
          operator: f.operator,
          value: f.value,
        };
      })
      .filter((f) => f?.value !== '');

    if (status !== undefined) {
      this.statusFilter = {
        field: 'status',
        operator: FilterOperator.Equals,
        value: status?.toString(),
      };
      this.searchOptions.advancedFilter = {
        logic: 'and',
        filters:
          this.rangeFilter && this.multiFilters.length
            ? [this.statusFilter, ...this.multiFilters, ...this.rangeFilter]
            : !this.rangeFilter && this.multiFilters.length
            ? [this.statusFilter, ...this.multiFilters]
            : this.rangeFilter && !this.multiFilters.length
            ? [this.statusFilter, ...this.rangeFilter]
            : [this.statusFilter],
      };
    } else {
      this.statusFilter = null;
      if (this.rangeFilter) {
        this.searchOptions.advancedFilter = {
          logic: 'and',
          filters: this.multiFilters.length
            ? [...this.multiFilters, ...this.rangeFilter]
            : [...this.rangeFilter],
        };
      } else {
        if (this.multiFilters.length) {
          this.searchOptions.advancedFilter = {
            logic: 'and',
            filters: [...this.multiFilters],
          };
        } else {
          this.searchOptions.advancedFilter = null;
        }
      }
    }
    this.getOrders(this.searchOptions);
  }

  getPageSettings(pageSettings: { perPage: number; currentPage: number }) {
    this.searchOptions.pageSize = pageSettings.perPage;
    this.searchOptions.pageNumber = pageSettings.currentPage;
    this.getOrders(this.searchOptions, false);
  }

  searchOrder() {
    this.search.next(this.searchString.toLowerCase());
  }

  sortOrders(
    field: string,
    isCustom: boolean | undefined,
    type: FieldType | undefined,
    id: string | undefined
  ) {
    if (isCustom) {
      return;
    }
    if (field === 'countryId') {
      field = 'country.name';
    }
    if (!this.isOrdersLoaded) {
      return;
    }
    if (field !== this.selectedSort.field) {
      if (isCustom) {
        this.selectedSort = { field, mode: 'asc' };
        const ft =
          type === 0 || type === 3
            ? 'FieldValueStr'
            : type === 1
            ? 'FieldValueNum'
            : 'FieldValueDate';
        this.searchOptions.orderBy = [
          `DynamicFields.Where(q => q.DynamicFieldId == \"${id}\").Select(s => s.${ft}).FirstOrDefault()`,
        ];
        this.getOrders(this.searchOptions);
        return;
      }
      this.selectedSort = { field, mode: 'asc' };
      if (field === 'stage') {
        this.searchOptions.orderBy = ['Stage', 'IsCompleted'];
      } else {
        this.searchOptions.orderBy = [`${this.selectedSort.field}`];
      }
      this.getOrders(this.searchOptions);
      return;
    }
    if (this.selectedSort.mode === 'asc') {
      if (isCustom) {
        this.selectedSort = { field, mode: 'desc' };
        const ft =
          type === 0
            ? 'FieldValueStr'
            : type === 1
            ? 'FieldValueNum'
            : 'FieldValueDate';
        this.searchOptions.orderBy = [
          `DynamicFields.Where(q => q.DynamicFieldId == \"${id}\").Select(s => s.${ft}).FirstOrDefault() desc`,
        ];
        this.getOrders(this.searchOptions);
        return;
      }
      this.selectedSort = { field, mode: 'desc' };
      if (field === 'stage') {
        this.searchOptions.orderBy = [
          `Stage ${this.selectedSort.mode}`,
          `IsCompleted ${this.selectedSort.mode}`,
        ];
      } else {
        this.searchOptions.orderBy = [
          `${this.selectedSort.field} ${this.selectedSort.mode}`,
        ];
      }

      this.getOrders(this.searchOptions);
      return;
    }
    if (this.selectedSort.mode === 'desc') {
      this.selectedSort = { field: '', mode: '' };
      this.searchOptions.orderBy = [];
      this.getOrders(this.searchOptions);
      return;
    }
  }

  selectCol(col: string) {
    if (['stage', 'status'].includes(col)) {
      this.filteredFieldType = FieldType.List;
    }

    if (['poNumber'].includes(col)) {
      this.filteredFieldType = FieldType.Number;
    }

    if (['consigneeName', 'customerName', 'coldStorageName'].includes(col)) {
      this.filteredFieldType = FieldType.Text;
    }

    if (['createdOn', 'lastModifiedOn'].includes(col)) {
      this.filteredFieldType = FieldType.DateTime;
    }

    if (
      ![
        'poNumber',
        'stage',
        'createdOn',
        'lastModifiedOn',
        'status',
        'consigneeName',
        'customerName',
        'coldStorageName',
      ].includes(col)
    ) {
      this.filteredFieldType = <FieldType>(
        this.dynamicFields.find(
          (f: OrderField) => f.name.toLowerCase() === col.toLowerCase()
        )?.type
      );
    }
    this.selectedCol = col;
  }

  toggleFilterOptions() {
    this.showFilterModal = !this.showFilterModal;
    setTimeout(() => {
      document
        .getElementById(this.selectedCol)
        ?.firstElementChild?.classList.add('active-li');
    }, 100);
  }

  dragItem(e: DragEvent, el: string) {
    const elPos = this.reorderedColumns.indexOf(el);
    const elState = this.columnsStatesBackup[elPos];
    const dataForTransfer = { element: el, elementState: elState };
    e.dataTransfer?.setData('text/plain', JSON.stringify(dataForTransfer));
  }

  dropItem(e: DragEvent) {
    const transferedData = JSON.parse(e.dataTransfer?.getData('text/plain')!);
    const dropEl = transferedData.element;
    const dropElState = transferedData.elementState;
    const dropPos = this.reorderedColumns.indexOf(dropEl);
    const targetId = (e.currentTarget as HTMLElement).id;
    const targetPos = this.reorderedColumns.indexOf(targetId);
    this.reorderedColumns.splice(dropPos, 1);
    this.reorderedColumns.splice(targetPos, 0, dropEl);
    this.columnsStatesBackup.splice(dropPos, 1);
    this.columnsStatesBackup.splice(targetPos, 0, dropElState);
    this.columnsStates = [...this.columnsStatesBackup];
  }

  disableDefaultBehavior(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  showHideColumn(index: number, col: string, event: MatCheckboxChange) {
    this.columnsStatesBackup[index] = !this.columnsStatesBackup[index];

    if (!event.checked) {
      this.allFieldFilters.forEach((f) => {
        if (f.name === col) {
          f.isShow = false;
          f.value = '';
          f.operator = '';
        }
      });
    } else {
      this.allFieldFilters.forEach((f) => {
        if (f.name === col) {
          f.isShow = true;
        }
      });
    }
  }

  resetMultiFilters() {
    this.allFieldFilters.forEach((f) => {
      f.value = '';
      f.operator = 'Equals';
    });
    this.multiFilters = null;
    this.isApplyFilters = true;
    this.applyMultiFilters();
  }

  applyFilters() {
    this.tableColumns = [...this.reorderedColumns];
    this.columnsStates = [...this.columnsStatesBackup];

    let hiddenColumns: string[] = [];

    this.columnsStates.forEach((s, i) => {
      if (!s) {
        hiddenColumns.push(this.tableColumns[i]);
      }
    });

    this.tableColumns = this.tableColumns.filter(
      (t) => !hiddenColumns.includes(t)
    );

    this.tableColumnsObj = this.reorderedColumnsObj.filter(
      (t) => !hiddenColumns.includes(t.columnDef)
    );

    this.tableColumnsObj.sort(
      (a: OrderTableColumn, b: OrderTableColumn) =>
        this.tableColumns.indexOf(a.columnDef) -
        this.tableColumns.indexOf(b.columnDef)
    );

    this.displayedColumns = this.tableColumnsObj.map((c) => c.columnDef);

    const tableSettings = {
      layoutVersion: COLUMN_LAYOUT_VERSION,
      tableColumns: this.tableColumns,
      reorderedColumns: this.reorderedColumns,
      columnsStates: this.columnsStates,
    };
    if (this.pageName === 'orders') {
      this.scopedSet('table', JSON.stringify(tableSettings));
    } else {
      this.scopedSet('reports_table', JSON.stringify(tableSettings));
    }

    setTimeout(() => {
      this.allFieldFilters.forEach((f) => {
        if (!this.tableColumns.includes(f.name)) {
          f.isShow = false;
        }
      });
    }, 1500);
  }

  applyMultiFilters() {
    setTimeout(() => {
      if (this.pageName === 'orders') {
        this.scopedRemove('multiFO');
        this.scopedSet('multiFO', JSON.stringify(this.allFieldFilters));
      } else {
        this.scopedRemove('multiFR');
        this.scopedSet('multiFR', JSON.stringify(this.allFieldFilters));
      }

      this.multiFilters = this.allFieldFilters
        .map((f) => {
          if (f.operator === 'Any') {
            return {
              logic: 'and',
              filters: [
                {
                  field: f.field,
                  operator: FilterOperator.NotEqual,
                  value: null,
                },
                {
                  field: f.field,
                  operator: FilterOperator.NotEqual,
                  value: 0,
                },
              ],
            };
          }

          if (
            (f.name === 'createdOn' || f.name === 'lastModifiedOn') &&
            f.operator === 'Equals' &&
            f.value
          ) {
            return {
              logic: 'and',
              filters: [
                {
                  field: f.field,
                  operator: FilterOperator.GreaterThanOrEqual,
                  value:
                    moment(f.value)
                      .startOf('day')
                      .format('YYYY-MM-DDTHH:mm:ss') + '.001Z',
                },
                {
                  field: f.field,
                  operator: FilterOperator.LessThanOrEqual,
                  value:
                    moment(f.value).endOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                    '.999Z',
                },
              ],
            };
          }

          if (
            (f.name === 'createdOn' || f.name === 'lastModifiedOn') &&
            f.operator === '!Equals' &&
            f.value
          ) {
            return {
              logic: 'or',
              filters: [
                {
                  field: f.field,
                  operator: FilterOperator.LessThan,
                  value:
                    moment(f.value)
                      .startOf('day')
                      .format('YYYY-MM-DDTHH:mm:ss') + '.001Z',
                },
                {
                  field: f.field,
                  operator: FilterOperator.GreaterThan,
                  value:
                    moment(f.value).endOf('day').format('YYYY-MM-DDTHH:mm:ss') +
                    '.999Z',
                },
              ],
            };
          }

          return {
            field: f.field,
            operator: f.operator,
            value: f.value,
          };
        })
        .filter((f) => f?.value !== '' && f.operator !== '');

      if (this.multiFilters.length) {
        this.searchOptions.pageNumber = 1;
        this.searchOptions.advancedFilter = {
          logic: 'and',
          filters:
            this.statusFilter && this.rangeFilter
              ? [this.statusFilter, ...this.multiFilters, ...this.rangeFilter]
              : this.statusFilter && !this.rangeFilter
              ? [this.statusFilter, ...this.multiFilters]
              : !this.statusFilter && this.rangeFilter
              ? [...this.multiFilters, ...this.rangeFilter]
              : [...this.multiFilters],
        };
        this.getSummary(this.orderStatus.Open, this.multiFilters);
        this.getSummary(this.orderStatus.OnHold, this.multiFilters);
        this.getSummary(this.orderStatus.Closed, this.multiFilters);
        this.getSummary(this.orderStatus.Canceled, this.multiFilters);
      } else {
        this.searchOptions.pageNumber = 1;
        this.searchOptions.advancedFilter = {
          logic: 'and',
          filters:
            this.statusFilter && this.rangeFilter
              ? [this.statusFilter, ...this.rangeFilter]
              : this.statusFilter && !this.rangeFilter
              ? [this.statusFilter]
              : !this.statusFilter && this.rangeFilter
              ? [...this.rangeFilter]
              : [...this.multiFilters],
        };
        this.getSummary(this.orderStatus.Open);
        this.getSummary(this.orderStatus.OnHold);
        this.getSummary(this.orderStatus.Closed);
        this.getSummary(this.orderStatus.Canceled);
      }

      this.isApplyFilters = false;
      this.getOrders(this.searchOptions);
    }, 1000);
  }

  cancelFiltering() {
    if (this.pageName === 'orders' && this.scopedGet('multiFO')) {
      const savedFO = [...JSON.parse(<string>this.scopedGet('multiFO'))];
      this.allFieldFilters.forEach((f) => {
        savedFO.forEach((fo) => {
          if (f.name === fo.name) {
            f.value = fo.value;
            f.operator = fo.operator;
          }
        });
      });
    }

    if (this.pageName !== 'orders' && this.scopedGet('multiFR')) {
      const savedFR = [...JSON.parse(<string>this.scopedGet('multiFR'))];
      this.allFieldFilters.forEach((f) => {
        savedFR.forEach((fr) => {
          if (f.name === fr.name) {
            f.value = fr.value;
            f.operator = fr.operator;
          }
        });
      });
    }
  }

  removeValue(field: any) {
    this.allFieldFilters.forEach((f) => {
      if (f.name === field.name) {
        f.value = '';
        f.operator = '';
      }
    });
  }

  resetFilters() {
    if (this.pageName === 'orders') {
      this.scopedRemove('table');
    } else {
      this.scopedRemove('reports_table');
    }
    this.reorderedColumns = [...this.reorderedColumnsB];
    this.columnsStatesBackup = [...this.columnsStatesBackupB];

    this.columnsStates = [...this.columnsStatesBackup];

    let hiddenColumns: string[] = [];

    this.columnsStates.forEach((s, i) => {
      if (!s) {
        hiddenColumns.push(this.tableColumns[i]);
      }
    });

    this.tableColumns = this.tableColumns.filter(
      (t) => !hiddenColumns.includes(t)
    );
    this.displayedColumns = this.tableColumnsObj.map((c) => c.columnDef);
  }

  formatName(name: string) {
    return name.split(/(?=[A-Z])/).join(' ');
  }

  hideModal(event: Event) {
    if (event.target === event.currentTarget) {
      this.cancelFiltering();
      this.showFilterModal = false;
    }
  }

  getAllOrders(pageNumber: number) {
    let searchOpts: FilterOptions = {
      advancedSearch: {
        fields: [],
        keyword: '',
      },
      keyword: '',
      advancedFilter: null,
      pageNumber,
      pageSize: 500,
      orderBy: ['stage desc'],
    };
    this.ordersService
      .getOrders(searchOpts)
      .pipe(
        combineLatestWith(this.orderFieldService.getFields()),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([resp, dynamicFields]) => {
          let o = (this.orders = resp.data.map((order: any) => {
            const fields = [];
            for (let field of dynamicFields) {
              fields.push({
                name: field.name,
                type: field.type,
                isCustom: true,
                value: order.dynamicFields.find(
                  (df: OrderDynamicField) => df.dynamicFieldId === field.id
                )?.fieldValueDate
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueDate
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueStr
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueStr
                  : order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    )?.fieldValueNum
                  ? order.dynamicFields.find(
                      (df: OrderDynamicField) => df.dynamicFieldId === field.id
                    ).fieldValueNum
                  : '',
              });
            }
            for (let f of fields) {
              order = {
                ...order,
                [f.name]: f,
              };
            }
            return order;
          }));

          this.ordersForXslx.push(...o);

          if (this.ordersForXslx.length < this.paginationData.totalCount) {
            this.getAllOrders(pageNumber + 1);
          } else {
            try {
              this.writeOrdersWorkbook();
            } catch (error) {
              this.toastr.error(
                error instanceof Error ? error.message : 'Export to Excel failed.',
                'Error'
              );
            } finally {
              this.endExport();
            }
          }
        },
        // Without this the export dies silently on any failed page request (a 401 once the
        // session is evicted, a 5xx, a dropped connection): the recursion stops, no file is
        // ever written and the loading overlay stays up forever. The HTTP error itself is
        // already surfaced by ErrorInterceptor; this just has to release the UI.
        error: () => {
          this.endExport();
        },
      });
  }

  // Builds and downloads Orders.xlsx.
  //
  // This used to force every order into the MatTable and hand the rendered table to
  // utils.table_to_sheet. At ~2000 orders on dev that froze the main thread for ~100s, which is
  // how a 60s E2E download budget got missed, and it made the file hostage to the table still
  // being on screen when the scrape finally ran. Rows are now generated from the data instead,
  // so nothing beyond the current page is ever rendered.
  //
  // Only the HEADER row still comes from the DOM (sheetRows: 1 reads the <thead> <tr> alone,
  // which is always rendered and costs nothing). That keeps the header byte-identical without
  // hard-coding the Material sort-icon ligatures that table_to_sheet picks up from it.
  private writeOrdersWorkbook() {
    const table = document.getElementById('orders-table');
    if (!table) {
      throw new Error(
        'The orders table is no longer on screen. Please try the export again.'
      );
    }

    const headerSheet: WorkSheet = utils.table_to_sheet(table, { sheetRows: 1 });
    const header: string[] =
      utils.sheet_to_json<string[]>(headerSheet, { header: 1, defval: '' })[0] ?? [];

    const rows = this.ordersForXslx.map((order: any) =>
      this.tableColumnsObj.map((column) => this.exportCellText(order, column))
    );

    const workSheet: WorkSheet = utils.aoa_to_sheet([header, ...rows]);
    const workBook: WorkBook = utils.book_new();
    utils.book_append_sheet(workBook, workSheet, 'Orders');
    writeFile(workBook, 'Orders.xlsx');
    this.getOrders(this.searchOptions);
  }

  // Reproduces the text table_to_sheet used to scrape out of one rendered cell, so the exported
  // file is unchanged. It mirrors the *ngIf blocks of the orders-table template one-for-one and
  // concatenates them in the same order — xlsx strips whitespace adjacent to tags, so the pieces
  // run together exactly as they do here. Keep this in step with the template's <td> markup.
  private exportCellText(order: any, column: OrderTableColumn): string {
    let text = '';

    if (column.columnDef === 'poNumber') {
      text += order.isNew ? 'NEW' : '';
      text += column.cell(order);
    }

    if (
      column.columnDef === 'orderNumber' ||
      column.columnDef === 'healthCertificateNumber'
    ) {
      text += column.cell(order) !== 'N/A' ? column.cell(order) : '-';
    }

    if (column.columnDef === 'status') {
      text += orderStatusLabel(order.status);
    }

    if (
      column.columnDef === 'createdOn' ||
      column.columnDef === 'lastModifiedOn'
    ) {
      // Two adjacent spans in the template, so no separator between date and time.
      text += this.datePipe.transform(column.cell(order), 'MM/dd/yyyy') ?? '';
      text += this.datePipe.transform(column.cell(order), 'hh:mm a') ?? '';
    }

    if (column.columnDef === 'stage') {
      text += this.exportStageText(order);
    }

    if (
      column.columnDef === 'customerName' ||
      column.columnDef === 'consigneeName' ||
      column.columnDef === 'coldStorageName'
    ) {
      text += `${this.nullTransform.transform(column.cell(order))}`;
    }

    if (column.columnDef === 'countryId') {
      text += column.cell_id(order);
    }

    if (column.isCustom) {
      text +=
        column.type === FieldType.DateTime
          ? this.datePipe.transform(
              column.cell_alt(order),
              'MM/dd/yyyy hh:mm a'
            ) ?? ''
          : column.cell_alt(order);
    }

    return text;
  }

  // The stage cell is a diagram, so what lands in the spreadsheet is whatever text happens to sit
  // in it: the display:none title (xlsx reads innerHTML, so hidden text counts, and its &nbsp;
  // decodes to a plain space), the stage label, and the Material ligature behind the done mark.
  // Reproduced verbatim to keep the file identical — see the export-format note in the commit.
  private exportStageText(order: any): string {
    let text = '';

    if (order.stage === OrderStage.Stage1) text += 'Stage 1  ';
    if (order.stage === OrderStage.Stage2A) text += 'Stage 2A  ';
    if (order.stage === OrderStage.Stage2B) text += 'Stage 2B  ';
    if (order.stage === OrderStage.Stage3) text += 'Stage 3  ';
    if (order.stage === OrderStage.Stage4) text += 'Stage 4  ';

    if (
      order.status === OrderStatus.Closed ||
      order.stage > OrderStage.Stage3
    ) {
      if (order.stage === OrderStage.Stage1) text += 'Customer';
      if (
        order.stage === OrderStage.Stage2A ||
        order.stage === OrderStage.Stage3
      ) {
        text += 'CM Inventory';
      }
      if (
        order.stage === OrderStage.Stage2B ||
        order.stage === OrderStage.Stage4
      ) {
        text += 'Cold Storage';
      }
    }

    if (order.status === OrderStatus.Closed) text += 'check_circle';

    return text;
  }

  // Shared teardown for every way an export can end, successful or not.
  private endExport() {
    this.ordersForXslx = [];
    this.loadingService.changeLoadingStatus(false);
    enableBodyScroll(this.exportModalEl.nativeElement);
  }

  exportToExcel() {
    this.loadingService.changeLoadingStatus(true);
    setTimeout(() => {
      disableBodyScroll(this.exportModalEl.nativeElement);
      this.getAllOrders(1);
    }, 500);
  }

  goToCustomer(event: Event, id: string) {
    event.stopPropagation();
    this.router.navigate(['dashboard/customers', id]);
  }

  selectLine(lineId: string) {
    const lines = document.querySelectorAll('.li-line');
    const line = document.getElementById(lineId);
    lines.forEach((line) => {
      line.firstElementChild?.classList.remove('active-li');
    });
    line?.firstElementChild?.classList.add('active-li');
  }

  hideCustomField(field: string): boolean {
    return ![
      'poNumber',
      'orderNumber',
      'stage',
      'status',
      'consigneeName',
      'customerName',
      'coldStorageName',
      'countryId',
      'createdOn',
      'lastModifiedOn',
    ].includes(field);
  }

  private updateHeaderBadge(): void {
    if (this.pageName !== 'orders' || !this.paginationData) {
      return;
    }
    const count = this.paginationData.totalCount;
    this.headerState.setBadge(
      `${count.toLocaleString('en-US')} ${count === 1 ? 'order' : 'orders'}`
    );
  }

  ngOnDestroy(): void {
    clearAllBodyScrollLocks();
    this.headerState.setBadge(null);
    this.destroy.next(true);
    this.destroy.complete();
  }
}
